<script context="module" lang="ts">
    import { get, writable } from "svelte/store"
    import { defaultVAT, maxInputLength } from "../../../../api/hooks/config-client"
    import {
        predefinedContact,
        type PredefinedContact,
        iframeView,
        preselectedDealerNumber,
        referrer,
    } from "../../store"

    const vat = defaultVAT
    const customerTypesSelectOptions = [
        {
            value: "end",
            localizationKey: "inquiry.customer-type.end",
            selected: true,
        },
        {
            value: "business",
            localizationKey: "inquiry.customer-type.business",
        },
    ]

    const predef = get(predefinedContact) || ({} as PredefinedContact)

    let _preselectedDealerNumbers = get(preselectedDealerNumber)
        ?.split(",")
        ?.map((n) => {
            return parseInt(n)
        })

    const inquiry = writable<ProductInquiry>({
        customerType: "end",
        desiredRetailPartner: "",
        dealerChoice: {
            number: 0,
            company: "no dealer",
            postcode: "00000",
        },
        account: null,
        contact: {
            email: predef.email || "",
            phone: predef.phone || "",
        },
        billingAddress: {
            firstname: predef.firstname || "",
            lastname: predef.lastname || "",
            company: predef.company || "",
            city: predef.city || "",
            street: predef.street || "",
            houseNumber: predef.houseNumber || "",
            postcode: predef.postcode || "",
            countryCode: predef.countryCode || "DE",
        },
        commission: "",
        constructionProject: "",
        cart: [],
        sum: {
            subtotalNet: 0,
            subtotalGross: 0,
            minQtyNet: 0,
            minQtyGross: 0,
            packagingNet: 0,
            packagingGross: 0,
            shippingNet: 0,
            shippingGross: 0,
            totalNet: 0,
            totalGross: 0,
            totalVAT: 0,
        },
        vat,
        comment: "",
        offerAssistant: null,
        referrer: {
            url: get(referrer),
            iframe: get(iframeView),
            preselectedDealerNumber: null,
            //            preselectedDealerNumber: isNaN(_preselectedDealerNumber) ? null : _preselectedDealerNumber,
        },
    })
</script>

<script lang="ts">
    import { _ } from "svelte-i18n"
    import FormInputText from "../widgets/FormInputText.svelte"
    import ChevronDownIcon from "svelte-material-icons/ChevronDown.svelte"

    import ProductSummary from "./ProductSummary.svelte"
    import {
        cryptchaSolution,
        cryptchaSolutionId,
        formatPrice,
        getProductOption,
        getProductPrice,
        initCryptcha,
        resetCryptcha,
    } from "../../utils"
    import { getCountries, getCountry, getDealer, postProductInquiry, postOfferAssistantInquiry } from "../../api"
    import MiniLoader from "../widgets/MiniLoader.svelte"
    import { colorGroup1 } from "../../config"
    import FormInputRadio from "../widgets/FormInputRadio.svelte"
    import { login } from "../../store"
    import { initialLocale, langField } from "../../i18n"
    import { onMount } from "svelte"
    import { setUser } from "../../sentry"

    let loading = false
    let success = false

    let active_acc1 = false
    let active_acc2 = false
    let dealerChoiceIsLocal = false
    let range = 50

    export let product: Product
    export let activeColor: RALColor = undefined
    export let activeOptions: string[] = []
    export let offerAssistantConfiguration: {
        configurationName: string
        configuration: ConfigurationOptionItem[]
        dealerInformation: string
    }
    export let submitInquirySuccessCallback: Function = () => {}

    let selectedCountry: Country
    $: if ($inquiry.billingAddress) {
        getCountry($inquiry.billingAddress.countryCode).then((c) => {
            selectedCountry = c
        })
    } else {
        selectedCountry = null
    }

    // only validate preselection for non dealer logins
    if ($preselectedDealerNumber && !$login) {
        getDealer({ dealerNumber: _preselectedDealerNumbers })
            .then(function (r) {
                if (r?.data?.length) {
                    $inquiry.dealerChoice = Object.assign({}, r.data[0], {
                        number: parseInt(r.data[0].number),
                    })
                }
            })
            .finally(function () {
                if (!$inquiry.dealerChoice?.number) {
                    // invalidate preselection because no db finding
                    $preselectedDealerNumber = null
                    _preselectedDealerNumbers = null
                }
            })

        $inquiry.customerType = "end"
    }
    if ($login?.account?.ident?.dealerNumber) {
        $inquiry.dealerChoice = {
            number: $login.account.ident.dealerNumber,
            postcode: null,
            company: null,
        }
    }

    let submitTried = false
    $: missingDealer = (!$inquiry.dealerChoice || !$inquiry.dealerChoice.number) && !$inquiry.desiredRetailPartner

    $: {
        if (product) {
            const cartItem: CartItem = {
                quantity: 1,
                product,
                options: product?.options?.filter((o) => {
                    return activeOptions?.indexOf(o?.option) != -1
                }),
                price: {
                    base: formatPrice(
                        $login ? product?.price_net_dealer : product?.price_net_end_customer,
                        0,
                        1,
                        false
                    ) as number,
                    options: 0,
                    unit: getProductPrice(product, activeColor, activeOptions, !!$login),
                },
            }

            if (activeColor) {
                const ralOption = getProductOption(product, "ral")
                if (ralOption) {
                    if (!cartItem.options) {
                        cartItem.options = []
                    }
                    const ralChoice: OptionEntry = {
                        option: "ral",
                        code: parseInt(activeColor.RAL),
                        price_net: 0,
                    }
                    let inGroup1 = false
                    colorGroup1.forEach((c) => {
                        if (c.RAL == activeColor.RAL) {
                            inGroup1 = true
                        }
                    })
                    if (!inGroup1) {
                        ralChoice.price_net = ralOption.price_net
                    }
                    cartItem.options.push(ralChoice)
                }
            }

            cartItem.options?.forEach((o) => {
                cartItem.price.options = formatPrice(cartItem.price.options + o.price_net, 0, 1, false) as number
            })

            const cart = [cartItem]

            const sum: CartSum = {
                subtotalNet: 0,
                subtotalGross: 0,
                minQtyNet: 0,
                minQtyGross: 0,
                packagingNet: 0,
                packagingGross: 0,
                shippingNet: 0,
                shippingGross: 0,
                totalNet: 0,
                totalGross: 0,
                totalVAT: 0,
            }

            cart.forEach((c) => {
                sum.subtotalNet += formatPrice(c.price.unit, 0, 1, false) as number
                sum.subtotalGross += formatPrice(c.price.unit, vat, 1, false) as number
            })
            sum.totalNet = sum.subtotalNet
            sum.totalGross = sum.subtotalGross
            sum.totalVAT = formatPrice(sum.totalGross - sum.totalNet, 0, 1, false) as number

            $inquiry.cart = cart
            $inquiry.sum = sum
        }

        // Offer Assistant - Data
        if (!product && offerAssistantConfiguration) {
            $inquiry.offerAssistant = offerAssistantConfiguration
        }
    }

    let x = ""

    async function submit() {
        // track in sentry
        setUser({
            username:
                $login?.account?.ident && $login.account.ident.dealerNumber + "-" + $login.account.ident.personNumber,
            email: $inquiry.contact?.email,
        })

        submitTried = true

        // Post OfferAssistant-Inquiry - ONLY DEBUG
        // if (!product && offerAssistantConfiguration) {
        //     await postOfferAssistantInquiry($inquiry)
        //     submitInquirySuccessCallback()
        // }

        if (
            !missingDealer ||
            (!selectedCountry.show_inquiry_local_store_select && !selectedCountry.show_inquiry_online_shop_select)
        ) {
            loading = true

            try {
                // @ts-ignore
                $inquiry._s = $cryptchaSolution
                // @ts-ignore
                $inquiry._sId = $cryptchaSolutionId

                if ($login) {
                    // no customerdata required for dealer logins
                    // dont set null, so later binding works
                    // @ts-ignore
                    $inquiry.billingAddress = {}
                    $inquiry.customerType = null

                    $inquiry.account = $login.account
                    // @ts-ignore
                    $inquiry.contact = {}
                }

                // restrict billingAddress and contact object for max length
                for (const key in $inquiry.billingAddress) {
                    // @ts-ignore
                    if ($inquiry.billingAddress[key]?.length > maxInputLength[key]) {
                        // @ts-ignore
                        $inquiry.billingAddress[key] = $inquiry.billingAddress[key].substring(0, maxInputLength[key])
                    }
                }
                for (const key in $inquiry.contact) {
                    // @ts-ignore
                    if ($inquiry.contact[key]?.length > maxInputLength[key]) {
                        // @ts-ignore
                        $inquiry.contact[key] = $inquiry.contact[key].substring(0, maxInputLength[key])
                    }
                }

                // Post Product-Inquiry
                if (product) {
                    await postProductInquiry(
                        $inquiry,
                        ((($inquiry.contact?.email?.length as number) || 1) * 1000).toString(16) + x
                    )
                }

                // Post OfferAssistant-Inquiry
                if (!product && offerAssistantConfiguration) {
                    await postOfferAssistantInquiry(
                        $inquiry,
                        ((($inquiry.contact?.email?.length as number) || 1) * 1000).toString(16) + x
                    )
                    submitInquirySuccessCallback()
                }

                success = true
            } catch (e) {
                // @ts-ignore
                if (e.data?.status == 403) {
                    alert($_("inquiry.error-alert"))
                } else {
                    console.log(e)
                    alert($_("inquiry.error-alert"))
                    throw new Error(JSON.stringify(e, null, 4))
                }
            } finally {
                loading = false
                resetCryptcha()
            }
        }
    }

    typeof window !== "undefined" &&
        window.setTimeout(() => {
            x = "x"
        }, 5000)

    let cryptchaEl: HTMLElement
    onMount(() => {
        initCryptcha(cryptchaEl)
    })

    // reset desired retail partner if dealer is selected
    let oldDealerNumber = $inquiry.dealerChoice?.number
    $: if (oldDealerNumber !== $inquiry.dealerChoice?.number && $inquiry.dealerChoice?.number) {
        oldDealerNumber = $inquiry.dealerChoice?.number
        $inquiry.desiredRetailPartner = ""
    }

    // only load dealers if there is a real change in data
    let _dealerChoiceQuery: {
        countrycode: string
        postcode: string
        dealerNumber: string | number | string[] | number[]
        range: number
        online?: boolean
    } = null
    let _oldCountryCode: string = null
    let _oldPostcode: string = null
    let _oldDealerNumbers: number[] = null
    $: if (
        _oldCountryCode !== $inquiry.billingAddress.countryCode ||
        (_oldPostcode !== $inquiry.billingAddress.postcode && $inquiry.billingAddress.postcode.length == 5) ||
        JSON.stringify(_oldDealerNumbers) !== JSON.stringify(_preselectedDealerNumbers)
    ) {
        _oldCountryCode = $inquiry.billingAddress.countryCode
        _oldPostcode = $inquiry.billingAddress.postcode
        _oldDealerNumbers = _preselectedDealerNumbers
        _dealerChoiceQuery = {
            countrycode: $inquiry.billingAddress.countryCode,
            postcode: $inquiry.billingAddress.countryCode == "DE" ? $inquiry.billingAddress?.postcode : null,
            dealerNumber: _preselectedDealerNumbers,
            range: 10000,
        }
    }

    $: if ($preselectedDealerNumber) {
        $inquiry.referrer.preselectedDealerNumber = $inquiry.dealerChoice?.number
    }

    $: console.log($inquiry)
</script>

<div class="content" class:iframe="{$iframeView}">
    {#if product}
        <ProductSummary {product} color="{activeColor}" options="{activeOptions}" />
    {/if}

    <div class="header">
        {#if success && offerAssistantConfiguration}
            <h3>{$_("inquiry.success-headline-offer-assistant")}</h3>
        {:else}
            <h3>{$_("inquiry.headline")}</h3>
        {/if}
    </div>

    {#if loading}
        <MiniLoader />
    {:else if success}
        {#if offerAssistantConfiguration}
            {$_("inquiry.success-offer-assistant")}
        {:else}
            {$_("inquiry.success")}
        {/if}
    {:else}
        <form
            on:submit|preventDefault="{() => {
                submit()
            }}"
        >
            <div class="leftForm">
                <section>
                    {#if !$preselectedDealerNumber && !$login}
                        <div class="row">
                            <div class="col-md-12">
                                <div class="select_holder no-label">
                                    <!-- svelte-ignore a11y-no-onchange -->
                                    <select
                                        name="delivery_country"
                                        bind:value="{$inquiry.customerType}"
                                        placeholder="Kundentyp"
                                        required
                                    >
                                        {#each customerTypesSelectOptions as customerType}
                                            <option value="{customerType.value}" selected="{customerType.selected}">
                                                {#if customerType.localizationKey}
                                                    {$_(customerType.localizationKey)}
                                                {/if}
                                            </option>
                                        {/each}
                                    </select>
                                    <div class="placeholder_overlay"></div>
                                    <div class="right_overlay">
                                        <ChevronDownIcon size="24px" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    {/if}
                    <div class="row">
                        {#if !$login}
                            <div class="col-md-6">
                                <FormInputText
                                    name="vorname"
                                    placeholder="{$_('inquiry.firstname')} {$inquiry.customerType === 'business'
                                        ? `(${$_('inquiry.optional')})`
                                        : ''}"
                                    bind:value="{$inquiry.billingAddress.firstname}"
                                    type="text"
                                    maxlength="{maxInputLength.firstname}"
                                    required="{$inquiry.customerType === 'end'}"
                                />
                            </div>
                            <div class="col-md-6">
                                <FormInputText
                                    name="nachname"
                                    placeholder="{$_('inquiry.lastname')} {$inquiry.customerType === 'business'
                                        ? `(${$_('inquiry.optional')})`
                                        : ''}"
                                    bind:value="{$inquiry.billingAddress.lastname}"
                                    type="text"
                                    maxlength="{maxInputLength.lastname}"
                                    required="{$inquiry.customerType === 'end'}"
                                />
                            </div>
                            <div class="col-md-12">
                                <FormInputText
                                    name="firma"
                                    placeholder="{$_('inquiry.company')} {$inquiry.customerType === 'end'
                                        ? `(${$_('inquiry.optional')})`
                                        : ''}"
                                    bind:value="{$inquiry.billingAddress.company}"
                                    type="text"
                                    maxlength="{maxInputLength.company}"
                                    required="{$inquiry.customerType === 'business'}"
                                />
                            </div>
                            <div class="col-md-6">
                                <FormInputText
                                    name="strasse"
                                    placeholder="{$_('inquiry.street')}"
                                    bind:value="{$inquiry.billingAddress.street}"
                                    type="text"
                                    required
                                    maxlength="{maxInputLength.street}"
                                />
                            </div>
                            <div class="col-md-6">
                                <FormInputText
                                    name="hausnummer"
                                    placeholder="{$_('inquiry.house-number')}"
                                    bind:value="{$inquiry.billingAddress.houseNumber}"
                                    type="text"
                                    required
                                    maxlength="{maxInputLength.houseNumber}"
                                />
                            </div>
                            <div class="col-md-5">
                                <!-- <FormInputText
                            name="land"
                            placeholder="{$_('inquiry.country')}"
                            value="{$_('inquiry.default-country')}"
                            type="text"
                            readonly
                            required
                        /> -->
                                <div class="select_holder no-label">
                                    <!-- svelte-ignore a11y-no-onchange -->
                                    <select
                                        name="delivery_country"
                                        bind:value="{$inquiry.billingAddress.countryCode}"
                                        required
                                    >
                                        {#await getCountries({ valid_for_inquiry: true }, "name." + initialLocale)}
                                            <option value="{$inquiry.billingAddress.countryCode}">
                                                <MiniLoader />
                                            </option>
                                        {:then countries}
                                            {#each countries.data as country}
                                                <option
                                                    value="{country.code}"
                                                    selected="{country.code == $inquiry.billingAddress.countryCode}"
                                                >
                                                    {langField(country.name, "")}
                                                </option>
                                            {/each}
                                        {/await}
                                    </select>
                                    <div class="placeholder_overlay">
                                        {$_("checkout.country")}
                                    </div>
                                    <div class="right_overlay">
                                        <ChevronDownIcon size="24px" />
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <FormInputText
                                    name="plz"
                                    placeholder="{$_('inquiry.postcode')}"
                                    bind:value="{$inquiry.billingAddress.postcode}"
                                    type="text"
                                    required
                                    pattern="{$inquiry.billingAddress.countryCode == 'DE' ? '^\\d{5}$' : undefined}"
                                    maxlength="{maxInputLength.postcode}"
                                />
                            </div>
                            <div class="col-md-4">
                                <FormInputText
                                    name="ort"
                                    placeholder="{$_('inquiry.city')}"
                                    bind:value="{$inquiry.billingAddress.city}"
                                    type="text"
                                    required
                                    maxlength="{maxInputLength.city}"
                                />
                            </div>
                        {/if}
                        {#if $login?.account?.email}
                            <div class="col-md-6">
                                <label for="inquiry_email">{$_("inquiry.email")}</label><br />
                                <small>{$login.account.email}</small>
                            </div>
                            <div class="col-md-6">
                                <label for="inquiry_phone">{$_("inquiry.phone")}</label><br />
                                <small>{$login.account.phone || "-"}</small>
                            </div>
                        {:else}
                            <div class="col-md-6">
                                <FormInputText
                                    name="email"
                                    placeholder="{$_('inquiry.email')}"
                                    bind:value="{$inquiry.contact.email}"
                                    type="email"
                                    required
                                    maxlength="{maxInputLength.email}"
                                />
                            </div>
                            <div class="col-md-6">
                                <FormInputText
                                    name="phone"
                                    placeholder="{$_('inquiry.phone')}"
                                    tooltip="{$_('inquiry.phone-tooltip')}"
                                    bind:value="{$inquiry.contact.phone}"
                                    type="tel"
                                    required
                                    maxlength="{maxInputLength.phone}"
                                />
                            </div>
                        {/if}
                        <div class="col-md-6">
                            <FormInputText
                                name="commission"
                                placeholder="{$_('inquiry.commission')} ({$_('inquiry.optional')})"
                                bind:value="{$inquiry.commission}"
                                type="text"
                                maxlength="{maxInputLength.commission}"
                            />
                        </div>
                        <div class="col-md-6">
                            <FormInputText
                                name="constructionProject"
                                placeholder="{$_('inquiry.construction-project')} ({$_('inquiry.optional')})"
                                bind:value="{$inquiry.constructionProject}"
                                type="text"
                                maxlength="{maxInputLength.constructionProject}"
                            />
                        </div>
                        <div class="col-md-12">
                            <label for="">{$_("inquiry.special-request")}</label>
                            <textarea
                                name="kommentar"
                                placeholder="{$iframeView
                                    ? $_('inquiry.special-request-placeholder-iframe')
                                    : $_('inquiry.special-request-placeholder')}"
                                rows="5"
                                bind:value="{$inquiry.comment}"
                            ></textarea>
                        </div>
                    </div>
                </section>
            </div>

            <div class="rightForm">
                {#if $preselectedDealerNumber && _preselectedDealerNumbers?.length == 1}
                    <section>
                        <p>
                            {$_("inquiry.dealer-preselected-intro")}
                        </p>
                        {$inquiry.dealerChoice?.company}
                    </section>
                {:else if $preselectedDealerNumber && _preselectedDealerNumbers?.length > 1}
                    <section>
                        <p>
                            {$_("inquiry.dealer-choice-intro")}
                        </p>
                        {#await getDealer(_dealerChoiceQuery)}
                            <MiniLoader />
                        {:then data}
                            {#each data.data || [] as d}
                                <!-- svelte-ignore a11y-click-events-have-key-events -->
                                <label
                                    style="font-size: 0.8em; padding-left: 30px; display: block; font-weight: bold;"
                                    on:click="{(e) => {
                                        $inquiry.dealerChoice = Object.assign({}, d, {
                                            number: parseInt(d.number),
                                        })
                                    }}"
                                >
                                    <input
                                        style="margin-left: -30px;"
                                        type="radio"
                                        checked="{$inquiry.dealerChoice?.number == parseInt(d.number)}"
                                    />
                                    {d.company}
                                    ({#if d.distance}{d.distance}km{/if}{#if d.city}{#if d.distance},
                                        {/if}{d.city}{/if})
                                </label>
                            {/each}
                        {/await}
                    </section>
                {:else if $login}
                    <section>
                        <p>
                            {$_("inquiry.dealer-preselected-login-intro")}
                        </p>
                        {$inquiry.dealerChoice?.number}
                        <!-- TODO derzeit kein Check, ob Vorlage und Preiskalkulation im eInfo vorhanden sind -->
                    </section>
                {:else if selectedCountry?.show_inquiry_local_store_select || selectedCountry?.show_inquiry_online_shop_select}
                    <section>
                        <p>
                            {$_("inquiry.dealer-choice-intro")}
                        </p>
                        <div
                            class="section_headline"
                            style="{missingDealer && submitTried ? 'color: var(--error); font-weight: bold' : ''}"
                        >
                            {$_("inquiry.dealer-choice-prompt")}
                        </div>
                        <div class="accordeon">
                            {#if selectedCountry.show_inquiry_local_store_select && $inquiry.billingAddress?.postcode}
                                <div class="acc_item">
                                    <div
                                        class="acc_trigger"
                                        class:active="{active_acc1}"
                                        on:click="{() => {
                                            active_acc1 = !active_acc1
                                            active_acc2 = false
                                        }}"
                                        on:keydown
                                    >
                                        <h4>{$_("inquiry.local-dealer")}</h4>
                                        {#if $inquiry.dealerChoice?.number && dealerChoiceIsLocal}
                                            <div class="info chosen">
                                                <strong>{$inquiry.dealerChoice.company}</strong>
                                            </div>
                                        {:else}
                                            <div class="info">
                                                {$_("inquiry.please-choose")}
                                            </div>
                                        {/if}
                                        <div class="icon">
                                            <ChevronDownIcon size="30px" />
                                        </div>
                                    </div>
                                    {#if active_acc1}
                                        <div class="acc_content">
                                            {#await getDealer($inquiry.billingAddress.countryCode == "DE" ? { countrycode: $inquiry.billingAddress.countryCode, postcode: $inquiry.billingAddress?.postcode, range } : { countrycode: $inquiry.billingAddress.countryCode })}
                                                <MiniLoader />
                                            {:then data}
                                                {#if $inquiry.billingAddress.countryCode == "DE"}
                                                    {$_("inquiry.within-radius-of")}
                                                    <div class="radiusChoice">
                                                        <div>
                                                            <FormInputRadio
                                                                label="20km"
                                                                value="{20}"
                                                                bind:group="{range}"
                                                            />
                                                        </div>
                                                        <div>
                                                            <FormInputRadio
                                                                label="50km"
                                                                value="{50}"
                                                                bind:group="{range}"
                                                            />
                                                        </div>
                                                        <div>
                                                            <FormInputRadio
                                                                label="100km"
                                                                value="{100}"
                                                                bind:group="{range}"
                                                            />
                                                        </div>
                                                        <div>
                                                            <FormInputRadio
                                                                label="200km"
                                                                value="{200}"
                                                                bind:group="{range}"
                                                            />
                                                        </div>
                                                    </div>
                                                {/if}
                                                <div class="options">
                                                    {#each data.data || [] as d}
                                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                                        <div
                                                            class="option"
                                                            on:click="{() => {
                                                                $inquiry.dealerChoice = Object.assign({}, d, {
                                                                    number: parseInt(d.number),
                                                                })
                                                                active_acc1 = false
                                                                dealerChoiceIsLocal = true
                                                            }}"
                                                        >
                                                            {d.company}
                                                            ({d.distance}km{#if d.city}, {d.city}{/if})
                                                        </div>
                                                    {:else}
                                                        {$_("inquiry.no-dealer-within-radius")}
                                                    {/each}
                                                </div>
                                            {:catch e}
                                                {$_("inquiry.no-dealer-for-postcode")}
                                            {/await}
                                        </div>
                                    {/if}
                                </div>
                                {#if selectedCountry.show_inquiry_online_shop_select}
                                    <div class="or">{$_("inquiry.or")}</div>
                                {/if}
                            {/if}
                            {#if selectedCountry.show_inquiry_online_shop_select}
                                <div class="acc_item">
                                    <div
                                        class="acc_trigger"
                                        class:active="{active_acc2}"
                                        on:click="{() => {
                                            active_acc2 = !active_acc2
                                            active_acc1 = false
                                        }}"
                                        on:keydown
                                    >
                                        <h4>{$_("inquiry.renz-online-partner")}</h4>
                                        {#if $inquiry.dealerChoice?.online_shop && !dealerChoiceIsLocal}
                                            <div class="info chosen">
                                                <strong>{$inquiry.dealerChoice.company}</strong>
                                            </div>
                                        {:else}
                                            <div class="info">
                                                {$_("inquiry.please-choose")}
                                            </div>
                                        {/if}
                                        <div class="icon">
                                            <ChevronDownIcon size="30px" />
                                        </div>
                                    </div>
                                    {#if active_acc2}
                                        <div class="acc_content">
                                            {#await getDealer( { countrycode: $inquiry.billingAddress.countryCode, online: true } )}
                                                <MiniLoader />
                                            {:then data}
                                                <div class="options">
                                                    {#each data.data || [] as d}
                                                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                                                        <div
                                                            class="option"
                                                            on:click="{() => {
                                                                $inquiry.dealerChoice = Object.assign({}, d, {
                                                                    number: parseInt(d.number),
                                                                })
                                                                active_acc2 = false
                                                                dealerChoiceIsLocal = false
                                                            }}"
                                                        >
                                                            {d.company}
                                                        </div>
                                                    {:else}
                                                        {$_("inquiry.no-dealer-within-radius")}
                                                    {/each}
                                                </div>
                                            {/await}
                                        </div>
                                    {/if}
                                </div>
                            {/if}
                        </div>
                        {#if $inquiry.customerType === "business"}
                            <div class="or">{$_("inquiry.or")}</div>
                            <div>
                                <label for="">{$_("inquiry.customer-type.desired-retail-partner-label")}</label>
                                <textarea
                                    name="desiredRetailPartner"
                                    placeholder="{$_('inquiry.customer-type.desired-retail-partner-placeholder')}"
                                    rows="{$iframeView ? 2 : 5}"
                                    bind:value="{$inquiry.desiredRetailPartner}"
                                    on:keyup="{() => {
                                        if ($inquiry.desiredRetailPartner) {
                                            $inquiry.dealerChoice = null
                                            active_acc1 = false
                                            active_acc2 = false
                                        }
                                    }}"
                                ></textarea>
                            </div>
                        {/if}
                    </section>
                {/if}

                <section>
                    <!-- <FormInputCheckbox label="" required>
                    Ich habe die
                    <a href="/service/widerruf">Widerufsbelehrung</a>
                    zur Kenntnis genommen
                </FormInputCheckbox> -->
                    <!-- <FormInputCheckbox label="" required> -->
                    <div class="privacyText">
                        {@html $_("inquiry.privacy-terms-checkbox-html")}
                    </div>
                    <!-- </FormInputCheckbox> -->
                    <!-- <FormInputCheckbox label="" required>
                    Ich habe die
                    <a href="/service/agb">Allgemeinen Geschäftsbedingungen</a>
                    zur Kenntnis genommen
                </FormInputCheckbox> -->
                    <div class="row" style="margin-top:30px;">
                        <div class="col-md-4">
                            <button class="btn btn-secondary" type="submit" disabled="{!$cryptchaSolution}"
                                >{$_("inquiry.send")}</button
                            >
                        </div>
                        <div class="col-md-8" bind:this="{cryptchaEl}"></div>
                    </div>
                </section>
            </div>
        </form>
    {/if}
</div>

<style lang="less">
    @import "../../css/variables.less";
    /* Accordeon */
    .accordeon {
        background: var(--comp--input--background);
        color: var(--comp--input--color);

        .or {
            margin: 5px 0px;
        }
        .acc_item {
            position: relative;
            margin-bottom: 0px;

            .acc_trigger {
                border: 1px solid var(--border-color-default);
                .border-radius(5px);
                position: relative;
                cursor: pointer;
                padding: 10px;
                .transition();

                h4 {
                    font-size: 1rem;
                    margin: 0px;
                }
                .info {
                    font-size: 0.8rem;
                    &.chosen {
                        color: var(--primary);
                    }
                }
                .icon {
                    position: absolute;
                    right: 0;
                    top: 0;
                    bottom: 0;
                    font-size: 30px;
                    width: 50px;
                    .flex();
                    align-items: center;
                    justify-content: center;
                    .transition();
                }
                &.active {
                    border-color: var(--primary);
                    .icon {
                        transform: rotate(180deg);
                    }
                }
            }
            .acc_content {
                position: absolute;
                top: 100%;
                width: 100%;
                height: auto;
                margin-top: -10px;
                z-index: 100;
                border-left: solid 1px var(--primary);
                border-right: solid 1px var(--primary);
                border-bottom: solid 1px var(--primary);
                padding: 20px;
                background: var(--comp--input--background);
                color: var(--comp--input--color);

                .options {
                    width: 100%;
                    .option {
                        cursor: pointer;
                        padding: 10px;
                        border-bottom: 1px solid var(--border-color-default);
                        .transition();
                        &:hover {
                            background: var(--comp--input--background-hover);
                        }
                        &:last-child {
                            border: 0;
                        }
                    }
                }
                // display: none;
            }
        }
    }

    .content {
        padding: 20px 20px 60px 20px;
    }
    .header {
        .flex();
        align-items: center;
        justify-content: space-between;
        margin-bottom: 30px;
    }
    h3 {
        margin: 0px;
        margin-bottom: 0px;
    }

    section {
        padding: 20px 0px;
        border-bottom: 1px solid var(--border-color-default);
    }

    .section_headline {
        font-size: 1.2rem;
        margin-bottom: 15px;
    }
    :global .radio_group {
        .flex();
        .inputholder {
            margin-right: 20px;
        }
    }
    :global input,
    textarea {
        margin-bottom: 0px;
    }
    :global input:disabled {
        background: var(--comp--input--disabled--background);
    }
    :global .select_holder {
        position: relative;

        select {
            height: auto;
            padding-top: 20px;
            padding-bottom: 5px;
            -webkit-appearance: none;
            z-index: 1;
        }
        .placeholder_overlay {
            position: absolute;
            top: 3px;
            left: 21px;
            font-size: 0.7rem;
            color: inherit;
        }

        &.no-label {
            select {
                padding-top: 8px;
                padding-bottom: 7px;
            }
            .placeholder_overlay {
                display: none;
            }
        }

        .right_overlay {
            position: absolute;
            top: 10px;
            bottom: 10px;
            right: 1px;
            .flex();
            align-items: center;
            justify-content: center;
            width: 50px;
            border-left: solid 1px var(--border-color-2);
            z-index: 0;
            cursor: default;
            pointer-events: none;

            svg {
                color: inherit;
            }
        }
    }

    :global .inputholder input[type="checkbox"]:checked ~ label:before {
        background: var(--primary) !important;
    }
    :global .inputholder label:before {
        border-color: var(--primary) !important;
    }
    :global .inputholder input[type="radio"]:checked ~ label:after {
        background: var(--primary) !important;
    }

    div.radiusChoice {
        display: flex;
        gap: 30px;
    }

    @media (min-width: 1100px) {
        .iframe {
            .header {
                position: absolute;
                top: 14px;
                left: 55px;
            }
            .leftForm {
                width: 50%;
                float: left;
                padding-right: 30px;

                .col-md-3,
                .col-md-4,
                .col-md-5,
                .col-md-6,
                .col-md-12 {
                    padding: 5px;
                }
            }
            .rightForm {
                width: 50%;
                float: right;
                padding-left: 20px;
                border-left: 1px solid #ccc;

                .privacyText {
                    font-size: 0.7em;
                }
            }
        }
    }
</style>
